/**
 * Settlement results (#788): how the markets a user held settled. Pure (no React, no network); the shared fixture
 * shared/fixtures/settlement.json pins every function here so Android and iOS parse identically.
 *
 * Data (docs/research/SETTLEMENT_RESULTS.md):
 *   - the user's fills with dir "Settlement" (userFills / userFillsByTime): coin "#E" (older testnet: "@<asset id>"),
 *     px = payout per share (1 won, 0 lost, anything in between for a split settlement), sz = shares, time = when
 *     it settled, fee + feeToken, closedPnl. Fills of perps/spot ("BTC", "PURR") with dir "Settlement" are delistings,
 *     not outcomes, and are ignored here.
 *   - POST /info {"type":"settledOutcome","outcome":N}: { spec, settleFraction, details, question? } for a settled
 *     outcome (null while it is live). Settled outcomes drop out of outcomeMeta, so this is the only source of
 *     their names. `question.question` is {"settled": id} or {"active": id} (a named outcome settled early inside
 *     a question that is still trading).
 * Outcome ids, sides and coins come from hl/outcomes.ts only.
 */
import { outcomeTitle, type DescribeOptions } from '../describe';
import { formatWithUnit, priceUnit, roundDecimal } from '../describe/format';
import { parseDescription } from '../describe/parse';
import { fromSettlementCoin, sideName, type Side } from '../hl/outcomes';
import type { OutcomeSpec, QuestionSpec, UserFill } from '../hl/types';
import { add, cmp, decToString, mul, ONE, parseDec, sub, ZERO, type Dec } from '../trading/decimal';

export const SETTLEMENT_DIR = 'Settlement';

/** A user's Settlement fill on an outcome coin, decimals normalized ("20.0" → "20"). */
export interface SettlementFill {
  tid: number;
  hash: string;
  coin: string;
  outcome: number;
  side: Side;
  /** ms epoch of the settlement. */
  time: number;
  /** Shares settled (fill sz). */
  shares: string;
  /** USDC (feeToken) paid per share: fill px. */
  payoutPerShare: string;
  /** shares * payoutPerShare. */
  gross: string;
  fee: string;
  feeToken: string;
  /** gross - fee: what landed in the balance. */
  net: string;
  /**
   * Hyperliquid's closedPnl for the fill: gross minus the position's cost basis at trade prices. Neither the
   * settlement fee nor any fee paid on the buys is deducted (#894, SETTLEMENT_RESULTS.md §1a). null when the fill
   * has no usable closedPnl.
   */
  closedPnl: string | null;
}

export type QuestionStatus = 'settled' | 'active';

/** Normalized /info settledOutcome response. */
export interface SettledOutcome {
  outcome: number;
  spec: OutcomeSpec;
  /** Fraction paid to side 0; side 1 gets 1 - settleFraction. */
  settleFraction: string;
  /** Raw details string from the settler ("price:79980", "template", "", free text). */
  details: string;
  question?: { question: number; status: QuestionStatus | string; name: string; description: string };
}

export type SettlementOutcomeKind = 'won' | 'lost' | 'partial';

/** One settlement row as the apps show it. */
export interface SettlementResult extends SettlementFill {
  /** won: paid 1 per share, lost: 0, partial: anything in between. */
  result: SettlementOutcomeKind;
  /** Market title (describe/), "Outcome #N" when settledOutcome is unknown. */
  title: string;
  /** Name of the side held ("Yes", "Ravens"). */
  sideName: string;
  sideLabels: [string, string];
  question?: number;
  questionStatus?: string;
  /** Latest settleFraction from settledOutcome (absent when unknown). */
  settleFraction?: string;
  /** Market-level result: "Yes won", "Ravens won", "Yes paid $0.25, No paid $0.75". */
  resultText: string;
  /** Human settlement detail ("Settlement price $79,980", or the settler's text); absent when empty/placeholder. */
  details?: string;
  /** settledOutcome now says something different from what this fill paid (venue correction). */
  corrected: boolean;
  /** settledOutcome was found for this outcome. */
  known: boolean;
  /** closedPnl − settlement fee, in feeToken (settlementProfit); null = unknown or inconsistent data ("—"). */
  profit: string | null;
}

function decStr(v: unknown): string | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const d = parseDec(v);
  return d ? decToString(d) : null;
}

const dec = (s: string): Dec => parseDec(s)!;

/** Parse one raw fill; null unless it is a well-formed Settlement fill on an outcome coin. */
export function parseSettlementFill(raw: unknown): SettlementFill | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Partial<Record<keyof UserFill, unknown>>;
  if (f.dir !== SETTLEMENT_DIR || typeof f.coin !== 'string') return null;
  const ref = fromSettlementCoin(f.coin);
  if (!ref) return null;
  const px = decStr(f.px);
  const sz = decStr(f.sz);
  const fee = decStr(f.fee ?? '0');
  if (px === null || sz === null || fee === null) return null;
  if (typeof f.time !== 'number' || !Number.isFinite(f.time) || typeof f.tid !== 'number') return null;
  const p = dec(px);
  if (cmp(p, ZERO) < 0 || cmp(p, ONE) > 0 || cmp(dec(sz), ZERO) <= 0) return null;
  const gross = mul(p, dec(sz));
  return {
    tid: f.tid,
    hash: typeof f.hash === 'string' ? f.hash : '',
    coin: f.coin,
    outcome: ref.outcome,
    side: ref.side,
    time: f.time,
    shares: sz,
    payoutPerShare: px,
    gross: decToString(gross),
    fee,
    feeToken: typeof f.feeToken === 'string' && f.feeToken ? f.feeToken : 'USDC',
    net: decToString(sub(gross, dec(fee))),
    closedPnl: decStr(f.closedPnl),
  };
}

/** Normalize a settledOutcome response; null for null / malformed responses or an id mismatch. */
export function parseSettledOutcome(raw: unknown, outcome: number): SettledOutcome | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const spec = r.spec as Partial<OutcomeSpec> | undefined;
  if (!spec || typeof spec !== 'object' || spec.outcome !== outcome) return null;
  const f = decStr(r.settleFraction);
  if (f === null || cmp(dec(f), ZERO) < 0 || cmp(dec(f), ONE) > 0) return null;
  const out: SettledOutcome = {
    outcome,
    spec: {
      outcome,
      name: typeof spec.name === 'string' ? spec.name : '',
      description: typeof spec.description === 'string' ? spec.description : '',
      sideSpecs: Array.isArray(spec.sideSpecs) ? spec.sideSpecs.filter((s) => s && typeof s.name === 'string') : [],
      quoteToken: typeof spec.quoteToken === 'string' ? spec.quoteToken : 'USDC',
      ...(typeof spec.venue === 'string' ? { venue: spec.venue } : {}),
      ...(typeof spec.deployerFeeScale === 'string' ? { deployerFeeScale: spec.deployerFeeScale } : {}),
    },
    settleFraction: f,
    details: typeof r.details === 'string' ? r.details : '',
  };
  const q = r.question as Record<string, unknown> | undefined;
  if (q && typeof q === 'object' && q.question && typeof q.question === 'object') {
    const entries = Object.entries(q.question as Record<string, unknown>);
    const [status, id] = entries[0] ?? [];
    if (typeof status === 'string' && typeof id === 'number' && Number.isSafeInteger(id)) {
      out.question = {
        question: id,
        status,
        name: typeof q.name === 'string' ? q.name : '',
        description: typeof q.description === 'string' ? q.description : '',
      };
    }
  }
  return out;
}

/** Payout per share → won / lost / partial. */
export function resultKind(payoutPerShare: string): SettlementOutcomeKind {
  const p = parseDec(payoutPerShare);
  if (!p) return 'lost';
  if (cmp(p, ONE) >= 0) return 'won';
  if (cmp(p, ZERO) <= 0) return 'lost';
  return 'partial';
}

/** What a share of `side` pays under `settleFraction`. */
export function sidePayout(settleFraction: string, side: Side): string {
  const f = dec(settleFraction);
  return decToString(side === 0 ? f : sub(ONE, f));
}

/** Exact "$0.25" / "$1.00" (half away from zero, no float). */
export function usd2(v: string): string {
  const r = roundDecimal(v, 2);
  const neg = r.startsWith('-');
  const [i = '0', f = ''] = (neg ? r.slice(1) : r).split('.');
  return `${neg ? '-' : ''}$${i}.${f.padEnd(2, '0')}`;
}


/** "Yes won" / "No won" / "Yes paid $0.25, No paid $0.75". */
export function resultText(settleFraction: string, sideLabels: readonly [string, string]): string {
  const f = dec(settleFraction);
  if (cmp(f, ONE) === 0) return `${sideLabels[0]} won`;
  if (cmp(f, ZERO) === 0) return `${sideLabels[1]} won`;
  return `${sideLabels[0]} paid ${usd2(sidePayout(settleFraction, 0))}, ${sideLabels[1]} paid ${usd2(sidePayout(settleFraction, 1))}`;
}

const PLACEHOLDER_DETAILS = new Set(['', 'template', 'n/a']);

/** Settler's details for display: "price:79980" → "Settlement price $79,980"; placeholders → undefined. */
export function detailsText(s: SettledOutcome): string | undefined {
  const d = s.details.trim();
  if (PLACEHOLDER_DETAILS.has(d.toLowerCase())) return undefined;
  const m = /^price:([-+]?\d+(?:\.\d+)?)$/.exec(d);
  if (m && m[1] !== undefined) {
    const own = parseDescription(s.spec.description);
    const q = parseDescription(s.question?.description ?? '');
    const underlying = own.underlying ?? own.perp ?? q.underlying ?? q.perp;
    const unit = priceUnit(underlying, own.priceDescription ?? q.priceDescription);
    return `Settlement price ${formatWithUnit(m[1], unit)}`;
  }
  return d;
}

function questionSpecOf(s: SettledOutcome): QuestionSpec | undefined {
  if (!s.question) return undefined;
  return {
    question: s.question.question,
    name: s.question.name,
    description: s.question.description,
    fallbackOutcome: -1,
    namedOutcomes: [],
    settledNamedOutcomes: [s.outcome],
  };
}

export interface MarketResult {
  outcome: number;
  title: string;
  sideLabels: [string, string];
  settleFraction: string;
  resultText: string;
  details?: string;
  question?: number;
  questionStatus?: string;
}

/** Market-level view of a settled outcome (market page banner). */
/** `opts`: describe/ options (templates, timeZone, now) for the title. */
export function marketResult(s: SettledOutcome, opts: DescribeOptions): MarketResult {
  const sideLabels: [string, string] = [sideName(s.spec, 0), sideName(s.spec, 1)];
  const q = questionSpecOf(s);
  return {
    outcome: s.outcome,
    title: outcomeTitle(s.spec, s.outcome, q ?? null, opts),
    sideLabels,
    settleFraction: s.settleFraction,
    resultText: resultText(s.settleFraction, sideLabels),
    details: detailsText(s),
    question: s.question?.question,
    questionStatus: s.question?.status,
  };
}

/**
 * One settlement row: the fill (what was paid, always authoritative for money) + the latest settledOutcome (names,
 * market result, details). `settled` null → generic labels and resultText from the payout.
 */
export function settlementResult(fill: SettlementFill, settled: SettledOutcome | null, opts: DescribeOptions): SettlementResult {
  const known = !!settled && settled.outcome === fill.outcome;
  const m = known ? marketResult(settled, opts) : null;
  const sideLabels: [string, string] = m ? m.sideLabels : ['Yes', 'No'];
  // Without settledOutcome the market-level fraction follows from what this side paid.
  const fraction = m ? m.settleFraction : fill.side === 0 ? fill.payoutPerShare : sidePayout(fill.payoutPerShare, 1);
  const corrected = m ? cmp(dec(sidePayout(m.settleFraction, fill.side)), dec(fill.payoutPerShare)) !== 0 : false;
  return {
    ...fill,
    result: resultKind(fill.payoutPerShare),
    title: m ? m.title : `Outcome #${fill.outcome}`,
    sideName: sideLabels[fill.side],
    sideLabels,
    ...(m?.question !== undefined ? { question: m.question, questionStatus: m.questionStatus } : {}),
    ...(m ? { settleFraction: m.settleFraction } : {}),
    resultText: resultText(fraction, sideLabels),
    ...(m?.details !== undefined ? { details: m.details } : {}),
    corrected,
    known,
    profit: settlementProfit(fill),
  };
}

/** Settlement fills newest first (time, then tid), deduped by tid; later copies of a tid replace earlier ones. */
export function mergeSettlementFills(existing: readonly SettlementFill[], incoming: readonly SettlementFill[]): SettlementFill[] {
  const byTid = new Map<number, SettlementFill>();
  for (const f of existing) byTid.set(f.tid, f);
  for (const f of incoming) byTid.set(f.tid, f);
  return [...byTid.values()].sort((a, b) => b.time - a.time || b.tid - a.tid);
}

/** Parse a raw fill list (any dirs) into settlement fills, newest first. */
export function settlementFillsOf(raw: readonly unknown[]): SettlementFill[] {
  return mergeSettlementFills(
    [],
    raw.map(parseSettlementFill).filter((f): f is SettlementFill => f !== null),
  );
}

/**
 * Profit of one settled position (#894): closedPnl − settlement fee, in the fee token.
 *
 * closedPnl = payout (shares × payout per share) − cost basis, where the cost basis is the average buy price × the
 * shares settled, at trade prices. Verified on real fills (SETTLEMENT_RESULTS.md §1a): it includes no fee at all,
 * neither the settlement fee nor fees paid on the buys (Hyperliquid's own fee on an outcome buy is 0; a builder fee
 * is charged on top and is not in the cost basis). Gains or losses on shares sold before settlement are in those
 * Sell fills, not here.
 *
 * null ("—") when closedPnl is missing, or when it is inconsistent with the fill: the implied cost basis
 * (gross − closedPnl) must lie in [0, shares], since a share costs between $0 and $1.
 */
export function settlementProfit(fill: Pick<SettlementFill, 'closedPnl' | 'fee' | 'gross' | 'shares'>): string | null {
  if (typeof fill.closedPnl !== 'string') return null;
  const pnl = parseDec(fill.closedPnl);
  const fee = parseDec(fill.fee);
  const gross = parseDec(fill.gross);
  const shares = parseDec(fill.shares);
  if (!pnl || !fee || !gross || !shares) return null;
  const cost = sub(gross, pnl);
  if (cmp(cost, ZERO) < 0 || cmp(cost, shares) > 0) return null;
  return decToString(sub(pnl, fee));
}

/** "+$1.23" / "-$1.23" / "$0.00" (an amount that rounds to zero has no sign). */
export function signedUsd2(v: string): string {
  const r = usd2(v);
  if (/^-?\$0\.00$/.test(r)) return '$0.00';
  return r.startsWith('-') ? r : `+${r}`;
}

/** Hyperliquid documents that only the 10,000 most recent fills are reachable by time (userFillsByTime). */
export const FILL_REACH_LIMIT = 10_000;

/**
 * May older settlements be missing from the history? Sticky once true. A scan that saw FILL_REACH_LIMIT or more
 * distinct fills may have started past the oldest reachable one; fills dropped by the cache cap are gone too.
 */
export function historyTruncated(prev: boolean, rowsScanned: number, fillsDropped: number): boolean {
  return prev || rowsScanned >= FILL_REACH_LIMIT || fillsDropped > 0;
}

export interface SettlementSummary {
  /** Distinct settled markets (outcomes); holding both sides of one market is one market. */
  markets: number;
  /** Settled positions (rows). */
  positions: number;
  /** Rows by result. */
  won: number;
  lost: number;
  partial: number;
  /** Total profit of the USDC rows with a known profit; null when no row counts. */
  profit: string | null;
  /** Rows left out of the total: profit unknown ("—") or paid in another token (older USDH markets). */
  excluded: number;
  /** Older settlements may be missing (historyTruncated). */
  truncated: boolean;
}

/** Summary line above the Settled table, over the loaded rows. */
export function settlementSummary(
  rows: readonly Pick<SettlementResult, 'outcome' | 'result' | 'profit' | 'feeToken'>[],
  truncated: boolean,
): SettlementSummary {
  const markets = new Set<number>();
  let won = 0;
  let lost = 0;
  let partial = 0;
  let excluded = 0;
  let total: Dec | null = null;
  for (const r of rows) {
    markets.add(r.outcome);
    if (r.result === 'won') won++;
    else if (r.result === 'lost') lost++;
    else partial++;
    const p = r.profit === null || r.feeToken !== 'USDC' ? null : parseDec(r.profit);
    if (!p) {
      excluded++;
      continue;
    }
    total = total ? add(total, p) : p;
  }
  return {
    markets: markets.size,
    positions: rows.length,
    won,
    lost,
    partial,
    profit: total ? decToString(total) : null,
    excluded,
    truncated,
  };
}
